import React from 'react';

import { Button } from '@lightspeed/flame/Button';
import { Flex } from '@lightspeed/flame/Core';
import { ModalBody, ModalFooter, ModalHeader } from '@lightspeed/flame/Modal';
import { Select } from '@lightspeed/flame/Select';

import { createReactRenderer } from 'helpers/react-renderer';
import { DEFAULT_LANGUAGE, languageMap } from 'root/apps/Autocomplete/Languages/utils';

import { StyledModal } from './index.style';

export const PRINT_DOCUMENT_LANGUAGE_KEY = 'PRINT_DOCUMENT_LANGUAGE';

export const PrintedDocumentLanguages: React.FC<{
  callback: (language: MerchantosSupportedLanguages) => void;
  onClose?: () => void;
  buttonText?: string;
}> = ({ callback, onClose, buttonText }) => {
  const [isOpen, setIsOpen] = React.useState(true);
  const [enabledLanguages, setEnabledLanguages] = React.useState([DEFAULT_LANGUAGE]);
  const [language, setLanguage] = React.useState<MerchantosSupportedLanguages>(DEFAULT_LANGUAGE);

  const handleOnChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;

    setLanguage(value as MerchantosSupportedLanguages);
  };

  const handlePrint = () => {
    window.localStorage.setItem(PRINT_DOCUMENT_LANGUAGE_KEY, language!);

    callback(language);
    setIsOpen(false);
  };

  React.useEffect(() => {
    const languages = (window.merchantos.options.printedDocumentLanguages || [
      DEFAULT_LANGUAGE,
    ]) as MerchantosSupportedLanguages[];
    const localStorageLanguage =
      (window.localStorage.getItem(PRINT_DOCUMENT_LANGUAGE_KEY) as MerchantosSupportedLanguages) ||
      null;

    setLanguage(
      localStorageLanguage && languages.includes(localStorageLanguage)
        ? localStorageLanguage
        : languages[0]
    );
    setEnabledLanguages(languages);
  }, []);

  return (
    <StyledModal
      isOpen={isOpen}
      onRequestClose={() => {
        setIsOpen(false);
        onClose?.();
      }}
    >
      <ModalHeader data-test="printed-languages-select-modal">Select a language</ModalHeader>
      <ModalBody>
        <Flex alignItems="center" my={3}>
          <Select
            onChange={handleOnChange}
            value={language}
            style={{ minWidth: '200px' }}
            data-test="printed-languages-select"
          >
            <>
              {enabledLanguages.map((language) => (
                <option key={language} value={language}>
                  {languageMap[language as MerchantosSupportedLanguages]}
                </option>
              ))}
            </>
          </Select>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Flex justifyContent="flex-end" alignItems="center">
          <Button
            onClick={handlePrint}
            variant="secondary"
            fill
            disabled={!language}
            data-test="printed-languages-btn"
          >
            {buttonText || 'Print'}
          </Button>
        </Flex>
      </ModalFooter>
    </StyledModal>
  );
};

export const setPrintedDocumentLanguages = (props: {
  callback: (language: MerchantosSupportedLanguages) => void;
}) => {
  const RenderedComponent = createReactRenderer({
    component: PrintedDocumentLanguages,
    selector: '[data-react-root="ReactModal"]',
  });
  RenderedComponent.mount({ props });
};
